import React, { useEffect, useRef, useState } from 'react';
import { Edit, PackageSearch, X } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import styles from './SettingsPage.module.css';
import SearchableSelect from '../Shared/SearchableSelect';

const LOCATION_STATUSES = [
  ['awaiting_receipt', 'Ожидает оприходования'], ['awaiting_diagnosis', 'Ожидает диагностики'],
  ['in_diagnosis', 'На диагностике'], ['awaiting_repair', 'Ожидает ремонта'],
  ['in_repair', 'В ремонте'], ['awaiting_parts', 'Ожидание ЗИП'],
  ['awaiting_qc', 'Ожидает контроля качества'], ['in_qc', 'На контроле качества'],
  ['awaiting_cleaning', 'Ожидает клининга'], ['in_cleaning', 'На клининге'],
  ['in_packaging', 'На упаковке'], ['packaged', 'Упакован'],
  ['awaiting_shipping', 'Ожидает отправки'], ['returned', 'Возвращён в ремонт'], ['shipped', 'Отправлен'],
];
const REPAIR_STATUSES = [['', 'Не установлен'], ['in_repair', 'В ремонте'], ['ready', 'Готов'], ['return_without_repair', 'Возврат без ремонта']];

const emptyForm = {
  repair_request_id: '', serial_number: '', equipment_model: '', declared_fault: '',
  location_status: '', repair_status: '', current_basket: '', packaging_box_id: '',
  current_engineer: '', repair_start_date: '', repair_end_date: '', repair_details: '{}',
  parts_waiting_details: '{}', custom_fields: '{}',
};

const toLocalDateTime = value => value ? String(value).slice(0, 16) : '';
const parseJson = (value, label) => {
  if (!value.trim()) return null;
  try { return JSON.parse(value); } catch (_) { throw new Error(`${label}: некорректный JSON`); }
};

const RepairRequestEquipmentList = ({ searchQuery = '' }) => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [options, setOptions] = useState({ repair_requests: [], baskets: [], boxes: [], engineers: [] });
  const [optionsLoading, setOptionsLoading] = useState(false);
  const activeQuery = useRef('');
  const loadingRef = useRef(false);
  const requestVersion = useRef(0);

  const load = async (nextPage = 1, search = activeQuery.current, reset = false) => {
    if (loadingRef.current && !reset) return;
    const version = reset ? requestVersion.current + 1 : requestVersion.current;
    if (reset) {
      requestVersion.current = version;
      setItems([]);
      setCount(0);
    }
    try {
      loadingRef.current = true;
      setLoading(true);
      const { data } = await api.get('/repairs/equipment/', { params: { page: nextPage, search: search.trim() || undefined, settings_list: 1 } });
      if (version !== requestVersion.current) return;
      const rows = Array.isArray(data) ? data : (data.results || []);
      const total = Array.isArray(data) ? rows.length : (data.count || 0);
      setItems(previous => reset ? rows : [...previous, ...rows.filter(row => !previous.some(item => item.id === row.id))]);
      setCount(total);
      setPage(nextPage);
      setHasMore(Array.isArray(data) ? false : Boolean(data.next));
    } catch (error) {
      if (version === requestVersion.current) {
        setHasMore(false);
        toast.error('Не удалось загрузить оборудование заявок', { toastId: 'repair-equipment-load-error' });
      }
    } finally {
      if (version === requestVersion.current) {
        loadingRef.current = false;
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      activeQuery.current = searchQuery.trim();
      setHasMore(true);
      load(1, activeQuery.current, true);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  const handleListScroll = event => {
    const element = event.currentTarget;
    if (hasMore && !loadingRef.current && element.scrollHeight - element.scrollTop - element.clientHeight < 180) {
      load(page + 1);
    }
  };

  const openEdit = async item => {
    try {
      setOptionsLoading(true);
      const [{ data: fullItem }, { data: editOptions }] = await Promise.all([
        api.get(`/repairs/equipment/${item.id}/`),
        api.get(`/repairs/equipment/${item.id}/edit-options/`),
      ]);
      setOptions(editOptions);
      setEditing(fullItem);
    setForm({
      repair_request_id: fullItem.repair_request?.id || '', serial_number: fullItem.serial_number || '',
      equipment_model: fullItem.equipment_model || '', declared_fault: fullItem.declared_fault || '',
      location_status: fullItem.location_status || '', repair_status: fullItem.repair_status || '',
      current_basket: fullItem.current_basket || '', packaging_box_id: fullItem.packaging_box?.id || '',
      current_engineer: fullItem.current_engineer || '', repair_start_date: toLocalDateTime(fullItem.repair_start_date),
      repair_end_date: toLocalDateTime(fullItem.repair_end_date),
      repair_details: JSON.stringify(fullItem.repair_details || {}, null, 2),
      parts_waiting_details: JSON.stringify(fullItem.parts_waiting_details || {}, null, 2),
      custom_fields: JSON.stringify(fullItem.custom_fields || {}, null, 2),
    });
    } catch (error) {
      toast.error('Не удалось открыть оборудование', { toastId: 'repair-equipment-detail-error' });
    } finally {
      setOptionsLoading(false);
    }
  };

  const save = async event => {
    event.preventDefault();
    try {
      const payload = {
        repair_request_id: Number(form.repair_request_id), serial_number: form.serial_number.trim(),
        equipment_model: form.equipment_model.trim(), declared_fault: form.declared_fault,
        location_status: form.location_status, repair_status: form.repair_status || null,
        current_basket: form.current_basket ? Number(form.current_basket) : null,
        packaging_box_id: form.packaging_box_id ? Number(form.packaging_box_id) : null,
        current_engineer: form.current_engineer ? Number(form.current_engineer) : null,
        repair_start_date: form.repair_start_date || null, repair_end_date: form.repair_end_date || null,
        repair_details: parseJson(form.repair_details, 'Детали ремонта'),
        parts_waiting_details: parseJson(form.parts_waiting_details, 'Ожидание ЗИП'),
        custom_fields: parseJson(form.custom_fields, 'Дополнительные поля') || {},
      };
      await api.patch(`/repairs/equipment/${editing.id}/`, payload);
      toast.success('Оборудование обновлено');
      setEditing(null);
      load(1, activeQuery.current, true);
    } catch (error) {
      toast.error(error.message || error.response?.data?.detail || 'Ошибка сохранения оборудования');
    }
  };

  const field = (key, label, type = 'text') => (
    <div className={styles.formGroup}><label>{label}</label><input type={type} value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })} /></div>
  );
  const jsonField = (key, label) => (
    <div className={`${styles.formGroup} ${styles.fullWidth}`}><label>{label}</label><textarea rows="7" value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })} /></div>
  );
  const selectField = (key, label, values, emptyLabel = 'Не выбрано') => (
    <div className={styles.formGroup}><label>{label}</label><SearchableSelect
      options={values}
      value={form[key]}
      onChange={value => setForm({ ...form, [key]: value })}
      isDisabled={optionsLoading}
      isLoading={optionsLoading}
      placeholder={emptyLabel}
    /></div>
  );

  return <div className={styles.equipmentSettings}>
    <div className={styles.equipmentHeader}>
      <div><h3><PackageSearch size={24} /> Оборудование заявок</h3><p>Редактирование серийных номеров, моделей, статусов и служебных данных</p></div>
      <span className={styles.equipmentCount}>{count.toLocaleString('ru-RU')} записей</span>
    </div>
    {!loading && items.length === 0 ? <div className={styles.equipmentEmpty}><PackageSearch size={42} /><strong>Оборудование не найдено</strong><span>Измените запрос в верхней строке поиска</span></div> : <div className={`${styles.tableWrapper} ${styles.equipmentTableWrapper}`} onScroll={handleListScroll}><table className={styles.table}><thead><tr><th>ID</th><th>Заявка</th><th>Серийный номер</th><th>Модель</th><th>Статус</th><th>Ремонт</th></tr></thead><tbody>
      {items.map(item => <tr key={item.id} className={styles.clickableRow} onClick={() => openEdit(item)}><td>{item.id}</td><td>{item.repair_request?.request_number || item.repair_request?.id}</td><td>{item.serial_number}</td><td>{item.equipment_model}</td><td>{item.location_status_display}</td><td>{item.repair_status_display}</td></tr>)}
    </tbody></table>{loading && <div className={styles.equipmentLoading}><span></span>Загружаем следующую страницу…</div>}{!hasMore && items.length > 0 && <div className={styles.equipmentEnd}>Показаны все найденные записи</div>}</div>}

    {editing && <div className={styles.modalOverlay}><div className={styles.modalContent}><div className={styles.modalHeader}><h3 className={styles.modalTitle}><Edit size={20} /> Оборудование #{editing.id}</h3><button className={styles.modalCloseButton} onClick={() => setEditing(null)}><X size={20} /></button></div><div className={styles.modalBody}><form className={styles.form} onSubmit={save}>
      {selectField('repair_request_id', 'Заявка', options.repair_requests, 'Выберите заявку')}{field('serial_number', 'Серийный номер')}{field('equipment_model', 'Модель оборудования')}
      <div className={`${styles.formGroup} ${styles.fullWidth}`}><label>Заявленная неисправность</label><textarea value={form.declared_fault} onChange={e => setForm({ ...form, declared_fault: e.target.value })} /></div>
      <div className={styles.formGroup}><label>Статус перемещения</label><SearchableSelect options={LOCATION_STATUSES.map(([value, label]) => ({ value, label }))} value={form.location_status} onChange={value => setForm({ ...form, location_status: value })} /></div>
      <div className={styles.formGroup}><label>Статус ремонта</label><SearchableSelect options={REPAIR_STATUSES.map(([value, label]) => ({ value, label }))} value={form.repair_status} onChange={value => setForm({ ...form, repair_status: value })} /></div>
      {selectField('current_basket', 'Текущая корзина', options.baskets)}{selectField('packaging_box_id', 'Коробка упаковки', options.boxes)}{selectField('current_engineer', 'Ответственный инженер', options.engineers)}
      {field('repair_start_date', 'Начало ремонта', 'datetime-local')}{field('repair_end_date', 'Окончание ремонта', 'datetime-local')}
      {jsonField('repair_details', 'Детали ремонта (JSON)')}{jsonField('parts_waiting_details', 'Ожидание ЗИП (JSON)')}{jsonField('custom_fields', 'Дополнительные поля (JSON)')}
      <button className={styles.submitButton} type="submit">Сохранить</button>
    </form></div></div></div>}
  </div>;
};

export default RepairRequestEquipmentList;
